import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, FormControlLabel, IconButton, Switch, TextField, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import type { IContest, IProject, TContestGenre } from '../../../types/global/helper';
import { requirementsListToStringArray } from '../../../utils/stringUtils';
import { DraggableFieldList } from '../../../components/Common/DraggableFieldList';
import { mapWizardRequirementsToApi } from '../contestWizard/contestApiMappers';
import { RichTextViewer } from '../../../components/Common/RichTextViewer';
import { RichTextEditor } from '../../../components/Common/RichTextEditor';
import {
    criterionDisplayWeights,
    DrawerSectionEditorActions,
    editTriggerSx,
    sectionShellSx,
} from './BrandContestDetailsDrawerUi';
import { BrandDrawerEmptyMessage, brandChipSx, dashedFieldShellSx, FieldHint } from './brandContestDrawerFieldUtils';
import { CONTEST_API_GENRES, CONTEST_API_TAGS } from '../contestWizard/contestApiMappers';
import { editorialHeadlineSx, editorialInputSx, editorialLabelSx, midnight } from '../contestWizard/midnightStyles';
import { getContestApplicationCount, getContestMaxParticipants } from '../../../utils/contestParticipants';
import MediaAdderComponent from '../../../components/MediaAdderComponent/MediaAdderComponent';
import { ProjectPlayer } from '../../../components/EsCards/ProjectPlayer';
import { RectangularImageCropperModal } from '../../../components/ImageCropper/RectangularImageCropperModal';

type DisabledField = { disabled: boolean; message?: string };

export type DetailsEditSection =
    | 'identity'
    | 'narrative'
    | 'prerequisites'
    | 'discoverability'
    | 'campaign_video'
    | 'mobile_image'
    | 'application_capacity'
    | 'jury'
    | null;

function sortedStringArrayEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
}

export interface BrandContestDetailsDrawerBodyProps {
    contest: IContest;
    onSaveChanges: (payload: Record<string, unknown>) => void;
    saving?: boolean;
    disabled?: {
        title?: DisabledField;
        description?: DisabledField;
        requirements?: DisabledField;
        score_criteria?: DisabledField;
        slug?: DisabledField;
    };
}

export function BrandContestDetailsDrawerBody({
    contest,
    onSaveChanges,
    saving,
    disabled = {},
}: BrandContestDetailsDrawerBodyProps) {
    const [editing, setEditing] = useState<DetailsEditSection>(null);

    const [draftTitle, setDraftTitle] = useState(contest.title ?? '');
    const [draftDescription, setDraftDescription] = useState(contest.description ?? '');
    const [draftRequirements, setDraftRequirements] = useState<string[]>(() =>
        requirementsListToStringArray(contest?.requirements),
    );
    const [draftScoreCriteria, setDraftScoreCriteria] = useState<string[]>(() => [...(contest.score_criteria ?? [])]);
    const [newCriterion, setNewCriterion] = useState('');
    const [draftMaxParticipants, setDraftMaxParticipants] = useState(() =>
        contest.max_participants != null ? String(contest.max_participants) : '',
    );
    const [draftHideSubmissionsCount, setDraftHideSubmissionsCount] = useState(
        () => contest.hide_submissions_count === true,
    );
    const [draftTags, setDraftTags] = useState<string[]>(() => [...(contest.tag ?? [])]);
    const [draftGenres, setDraftGenres] = useState<TContestGenre[]>(() => [...(contest.genre ?? [])]);
    const [draftVideoMedia, setDraftVideoMedia] = useState<any[]>(() =>
        contest.contest_video ? [contest.contest_video] : [],
    );
    const mobileFileInputRef = useRef<HTMLInputElement>(null);
    const [mobileCropOpen, setMobileCropOpen] = useState(false);
    const [mobileCropSrc, setMobileCropSrc] = useState('');
    const [mobilePreviewUrl, setMobilePreviewUrl] = useState<string | null>(null);
    const [pendingMobileFile, setPendingMobileFile] = useState<File | null>(null);

    useEffect(() => {
        if (editing !== null) return;
        setDraftTitle(contest.title ?? '');
        setDraftDescription(contest.description ?? '');
        setDraftRequirements(requirementsListToStringArray(contest?.requirements));
        setDraftScoreCriteria([...(contest.score_criteria ?? [])]);
        setNewCriterion('');
        setDraftMaxParticipants(contest.max_participants != null ? String(contest.max_participants) : '');
        setDraftHideSubmissionsCount(contest.hide_submissions_count === true);
        setDraftTags([...(contest.tag ?? [])]);
        setDraftGenres([...(contest.genre ?? [])]);
        setDraftVideoMedia(contest.contest_video ? [contest.contest_video] : []);
        setMobilePreviewUrl(prev => {
            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
            return null;
        });
        setPendingMobileFile(null);
    }, [contest, editing]);

    useEffect(() => {
        return () => {
            if (mobilePreviewUrl?.startsWith('blob:')) {
                URL.revokeObjectURL(mobilePreviewUrl);
            }
            if (mobileCropSrc.startsWith('blob:')) {
                URL.revokeObjectURL(mobileCropSrc);
            }
        };
    }, [mobileCropSrc, mobilePreviewUrl]);

    const requirements = useMemo(() => requirementsListToStringArray(contest?.requirements), [contest?.requirements]);
    const criteria = useMemo(() => (contest.score_criteria ?? []).filter(Boolean), [contest.score_criteria]);
    const barWidths = useMemo(() => criterionDisplayWeights(criteria.map(String)), [criteria]);

    const displayName = contest.title?.trim() || 'Untitled campaign';

    const beginEdit = (section: Exclude<DetailsEditSection, null>) => {
        setDraftTitle(contest.title ?? '');
        setDraftDescription(contest.description ?? '');
        setDraftRequirements(requirementsListToStringArray(contest?.requirements));
        setDraftScoreCriteria([...(contest.score_criteria ?? [])]);
        setNewCriterion('');
        setDraftMaxParticipants(contest.max_participants != null ? String(contest.max_participants) : '');
        setDraftHideSubmissionsCount(contest.hide_submissions_count === true);
        setDraftTags([...(contest.tag ?? [])]);
        setDraftGenres([...(contest.genre ?? [])]);
        setDraftVideoMedia(contest.contest_video ? [contest.contest_video] : []);
        if (mobilePreviewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(mobilePreviewUrl);
        }
        setMobilePreviewUrl(null);
        setPendingMobileFile(null);
        setEditing(section);
    };

    const cancelEdit = () => setEditing(null);

    const saveIdentity = useCallback(() => {
        const t = draftTitle.trim();
        if (t !== (contest.title ?? '').trim()) {
            onSaveChanges({ title: t });
        }
        setEditing(null);
    }, [contest.title, draftTitle, onSaveChanges]);

    const saveNarrative = useCallback(() => {
        if ((draftDescription ?? '') !== (contest.description ?? '')) {
            onSaveChanges({ description: draftDescription });
        }
        setEditing(null);
    }, [contest.description, draftDescription, onSaveChanges]);

    const savePrerequisites = useCallback(() => {
        const next = draftRequirements.map(r => r.trim()).filter(Boolean);
        const current = requirementsListToStringArray(contest?.requirements);
        const changed = next.length !== current.length || next.some((line, index) => line !== current[index]);
        if (changed) {
            onSaveChanges({ requirements: mapWizardRequirementsToApi(next) });
        }
        setEditing(null);
    }, [contest?.requirements, draftRequirements, onSaveChanges]);

    const saveJury = useCallback(() => {
        onSaveChanges({ score_criteria: draftScoreCriteria.filter(r => r.trim().length > 0) });
        setEditing(null);
    }, [draftScoreCriteria, onSaveChanges]);

    const applicationCount = getContestApplicationCount(contest);
    const maxParticipants = getContestMaxParticipants(contest);

    const saveMaxParticipants = useCallback(() => {
        const v = draftMaxParticipants.trim();
        const next = v === '' ? null : Math.max(1, parseInt(v, 10) || 1);
        const cur = getContestMaxParticipants(contest);
        const payload: Record<string, unknown> = {};
        if (next !== cur) payload.max_participants = next;
        if (draftHideSubmissionsCount !== (contest.hide_submissions_count === true)) {
            payload.hide_submissions_count = draftHideSubmissionsCount;
        }
        if (Object.keys(payload).length) onSaveChanges(payload);
        setEditing(null);
    }, [contest, draftHideSubmissionsCount, draftMaxParticipants, onSaveChanges]);

    const saveDiscoverability = useCallback(() => {
        const payload: Record<string, unknown> = {};
        const curTags = contest.tag ?? [];
        const curGenres = contest.genre ?? [];
        if (!sortedStringArrayEqual(draftTags, curTags)) payload.tag = draftTags;
        if (!sortedStringArrayEqual(draftGenres, curGenres)) payload.genre = draftGenres;
        if (Object.keys(payload).length) onSaveChanges(payload);
        setEditing(null);
    }, [contest.genre, contest.tag, draftGenres, draftTags, onSaveChanges]);

    const saveCampaignVideo = useCallback(() => {
        const nextId = draftVideoMedia[0]?._id as string | undefined;
        const curId = contest.contest_video?._id;
        if (nextId !== curId) {
            onSaveChanges({ contest_video: nextId ?? null });
        }
        setEditing(null);
    }, [contest.contest_video?._id, draftVideoMedia, onSaveChanges]);

    const saveMobileFeatureImage = useCallback(() => {
        if (pendingMobileFile) {
            onSaveChanges({ mobile_feature_image: [pendingMobileFile] });
        }
        setEditing(null);
        setPendingMobileFile(null);
    }, [onSaveChanges, pendingMobileFile]);

    const handleMobileFilePick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (mobileCropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(mobileCropSrc);
        }
        setMobileCropSrc(URL.createObjectURL(file));
        setMobileCropOpen(true);
    };

    const handleMobileCropSave = (previewUrl: string, file: File) => {
        if (mobilePreviewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(mobilePreviewUrl);
        }
        setMobilePreviewUrl(previewUrl);
        setPendingMobileFile(file);
        setMobileCropOpen(false);
        if (mobileCropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(mobileCropSrc);
        }
        setMobileCropSrc('');
    };

    const handleMobileCropCancel = () => {
        setMobileCropOpen(false);
        if (mobileCropSrc.startsWith('blob:')) {
            URL.revokeObjectURL(mobileCropSrc);
        }
        setMobileCropSrc('');
    };

    const tagLabel = (value: string) => CONTEST_API_TAGS.find(t => t.value === value)?.label ?? value;
    const genreLabel = (value: TContestGenre) => CONTEST_API_GENRES.find(g => g.value === value)?.label ?? value;
    const displayMobileImage = mobilePreviewUrl || contest.mobile_feature_image;

    const sectionHeader = (
        title: string,
        actionLabel: string,
        section: Exclude<DetailsEditSection, null>,
        d?: DisabledField,
    ) => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2 }}>
            <Typography sx={{ ...editorialHeadlineSx, fontSize: 22, color: midnight.onSurface }}>{title}</Typography>
            {editing === section ? (
                <Box />
            ) : (
                <Button
                    type="button"
                    variant="text"
                    size="small"
                    disabled={d?.disabled}
                    onClick={() => beginEdit(section)}
                    sx={editTriggerSx}
                    title={d?.message}>
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                    {actionLabel}
                </Button>
            )}
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Campaign Identity */}
            <Box
                sx={{
                    ...sectionShellSx,
                    borderLeft: `4px solid ${midnight.primary}`,
                }}>
                {sectionHeader(
                    'Campaign identity',
                    'Edit entry',
                    'identity',
                    disabled.title?.disabled
                        ? {
                              disabled: true,
                              message: disabled.title?.message,
                          }
                        : undefined,
                )}
                {editing === 'identity' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <Typography sx={{ ...editorialLabelSx, mb: 0.75 }}>Display name</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                value={draftTitle}
                                onChange={e => setDraftTitle(e.target.value)}
                                disabled={disabled.title?.disabled}
                                helperText={disabled.title?.message}
                                sx={{
                                    ...editorialInputSx,
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: midnight.surfaceHighest,
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                        </Box>
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveIdentity}
                            saving={saving}
                            saveDisabled={!draftTitle.trim() || !!disabled.title?.disabled}
                        />
                    </Box>
                ) : (
                    <Box>
                        <Typography sx={{ ...editorialLabelSx, mb: 0.75 }}>Display name</Typography>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 500, color: midnight.onSurface }}>
                            {displayName}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Narrative */}
            <Box sx={sectionShellSx}>
                {sectionHeader('Campaign narrative', 'Edit narrative', 'narrative', disabled.description)}
                {editing === 'narrative' ? (
                    <Box>
                        <RichTextEditor
                            value={draftDescription}
                            onChange={setDraftDescription}
                            shellVariant="brandDashboard"
                        />
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveNarrative}
                            saving={saving}
                            saveDisabled={!!disabled.description?.disabled}
                        />
                    </Box>
                ) : contest.description?.trim() ? (
                    <Box
                        sx={{
                            '& .rich-text-wrapper .ql-editor': {
                                color: `${midnight.onSurfaceVariant} !important`,
                                lineHeight: 1.65,
                            },
                        }}>
                        <RichTextViewer content={contest.description} />
                    </Box>
                ) : (
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic' }}>
                        No narrative added yet.
                    </Typography>
                )}
            </Box>

            {/* Prerequisites */}
            <Box sx={sectionShellSx}>
                {sectionHeader('Creator prerequisites', 'Edit requirements', 'prerequisites', disabled.requirements)}
                {editing === 'prerequisites' ? (
                    <Box>
                        <FieldHint>Drag to reorder. Add requirements using the field below.</FieldHint>
                        <DraggableFieldList
                            items={draftRequirements}
                            onItemsChange={setDraftRequirements}
                            placeholder="Add requirement…"
                            inputSx={editorialInputSx}
                        />
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={savePrerequisites}
                            saving={saving}
                            saveDisabled={!!disabled.requirements?.disabled}
                        />
                    </Box>
                ) : requirements.length ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                        {requirements.map((line, idx) => (
                            <Box
                                key={`${idx}-${line.slice(0, 24)}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    bgcolor: midnight.surfaceContainer,
                                    p: 2,
                                    borderRadius: '8px',
                                }}>
                                <CheckCircleIcon sx={{ color: midnight.secondary, fontSize: 22 }} />
                                <Typography sx={{ fontWeight: 500, color: midnight.onSurface, fontSize: 14 }}>
                                    {line}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic' }}>
                        No prerequisites listed.
                    </Typography>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Tags & genre', 'Edit discoverability', 'discoverability')}
                <FieldHint>Add tags and genres so the right creators can find your campaign.</FieldHint>
                {editing === 'discoverability' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Box>
                            <Typography sx={{ ...editorialLabelSx, mb: 1 }}>Tags</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {CONTEST_API_TAGS.map(({ label, value }) => {
                                    const active = draftTags.includes(value);
                                    return (
                                        <Chip
                                            key={value}
                                            label={label}
                                            onClick={() =>
                                                setDraftTags(
                                                    active ? draftTags.filter(t => t !== value) : [...draftTags, value],
                                                )
                                            }
                                            sx={{
                                                bgcolor: active ? midnight.primary : midnight.surfaceHighest,
                                                color: active ? '#392700' : midnight.onSurface,
                                                fontWeight: 600,
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ ...editorialLabelSx, mb: 1 }}>Genre</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {CONTEST_API_GENRES.map(({ label, value }) => {
                                    const active = draftGenres.includes(value);
                                    return (
                                        <Chip
                                            key={value}
                                            label={label}
                                            onClick={() =>
                                                setDraftGenres(
                                                    active
                                                        ? draftGenres.filter(g => g !== value)
                                                        : [...draftGenres, value],
                                                )
                                            }
                                            sx={{
                                                bgcolor: active ? midnight.primary : midnight.surfaceHighest,
                                                color: active ? '#392700' : midnight.onSurface,
                                                fontWeight: 600,
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveDiscoverability}
                            saving={saving}
                        />
                    </Box>
                ) : (contest.tag?.length ?? 0) > 0 || (contest.genre?.length ?? 0) > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(contest.tag?.length ?? 0) > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.tag!.map(tag => (
                                    <Box key={tag} component="span" sx={brandChipSx}>
                                        {tagLabel(tag)}
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {(contest.genre?.length ?? 0) > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {contest.genre!.map(genre => (
                                    <Box key={genre} component="span" sx={brandChipSx}>
                                        {genreLabel(genre)}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <BrandDrawerEmptyMessage message="No tags or genres selected" />
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Campaign video', 'Edit video', 'campaign_video')}
                <FieldHint>Add a showcase video for your campaign (select from your projects).</FieldHint>
                {editing === 'campaign_video' ? (
                    <Box>
                        <MediaAdderComponent
                            mediaType="projects"
                            mediaValues={draftVideoMedia}
                            maxSelection={1}
                            visualVariant="brand"
                            title=""
                            onDelete={item => setDraftVideoMedia(draftVideoMedia.filter(m => m._id !== item._id))}
                            onSubmit={items => setDraftVideoMedia(items.slice(0, 1))}
                            disableAddButton
                        />
                        <DrawerSectionEditorActions onCancel={cancelEdit} onSave={saveCampaignVideo} saving={saving} />
                    </Box>
                ) : contest.contest_video?.hls ? (
                    <Box sx={{ maxWidth: 360, borderRadius: '10px', overflow: 'hidden' }}>
                        <ProjectPlayer project={contest.contest_video as IProject} onPlay={() => undefined} />
                    </Box>
                ) : (
                    <BrandDrawerEmptyMessage message="Select a video below when editing — it will appear here for contest management." />
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Mobile feature image', 'Edit image', 'mobile_image')}
                <FieldHint>
                    Shown on mobile when a dedicated mobile hero is set (falls back to desktop cover).
                </FieldHint>
                {editing === 'mobile_image' ? (
                    <Box>
                        <Box
                            sx={{
                                position: 'relative',
                                width: 200,
                                height: 200,
                                borderRadius: '10px',
                                overflow: 'hidden',
                                bgcolor: midnight.surfaceHighest,
                                border: '1px dashed rgba(72,72,71,0.45)',
                                mb: 2,
                            }}>
                            {displayMobileImage ? (
                                <Box
                                    component="img"
                                    src={displayMobileImage}
                                    alt="Mobile feature"
                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 13 }}>
                                        No image yet
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Button
                            type="button"
                            variant="outlined"
                            startIcon={<PhotoCameraOutlinedIcon />}
                            onClick={() => mobileFileInputRef.current?.click()}
                            sx={{
                                borderColor: 'rgba(255,201,101,0.45)',
                                color: midnight.primary,
                                borderRadius: 999,
                                textTransform: 'none',
                                fontWeight: 700,
                                mb: 2,
                            }}>
                            {displayMobileImage ? 'Replace image' : 'Upload image'}
                        </Button>
                        <input
                            ref={mobileFileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleMobileFilePick}
                        />
                        <RectangularImageCropperModal
                            open={mobileCropOpen}
                            src={mobileCropSrc}
                            onSave={handleMobileCropSave}
                            onCancel={handleMobileCropCancel}
                            aspect={1}
                            cropHeight={200}
                        />
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveMobileFeatureImage}
                            saving={saving}
                            saveDisabled={!pendingMobileFile}
                        />
                    </Box>
                ) : contest.mobile_feature_image ? (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('mobile_image')}
                        sx={{
                            ...dashedFieldShellSx,
                            p: 0,
                            width: 200,
                            height: 200,
                            overflow: 'hidden',
                        }}>
                        <Box
                            component="img"
                            src={contest.mobile_feature_image}
                            alt="Mobile feature"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </Box>
                ) : (
                    <Box
                        component="button"
                        type="button"
                        onClick={() => beginEdit('mobile_image')}
                        sx={{ ...dashedFieldShellSx, width: '100%', textAlign: 'left' }}>
                        <BrandDrawerEmptyMessage message="Click to upload a mobile feature image" />
                    </Box>
                )}
            </Box>

            <Box sx={sectionShellSx}>
                {sectionHeader('Application capacity', 'Edit limit', 'application_capacity')}
                <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 14, mb: 2 }}>
                    {applicationCount} application{applicationCount === 1 ? '' : 's'}
                    {maxParticipants != null ? ` · cap ${maxParticipants}` : ' · unlimited'}
                </Typography>
                {editing === 'application_capacity' ? (
                    <Box>
                        <Typography sx={{ ...editorialLabelSx, mb: 0.75 }}>Max applications (optional)</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            placeholder="Unlimited"
                            value={draftMaxParticipants}
                            onChange={e => setDraftMaxParticipants(e.target.value)}
                            sx={{
                                ...editorialInputSx,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: midnight.surfaceHighest,
                                    borderRadius: '10px',
                                },
                            }}
                        />
                        <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mt: 0.75, mb: 2 }}>
                            Leave empty for unlimited. When full, the public campaign page shows Sold Out.
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={draftHideSubmissionsCount}
                                    onChange={e => setDraftHideSubmissionsCount(e.target.checked)}
                                />
                            }
                            label="Hide public submission count & entry playlist"
                            sx={{ color: midnight.onSurface, mb: 0.5 }}
                        />
                        <Typography sx={{ color: midnight.onSurfaceVariant, fontSize: 12, mb: 2 }}>
                            Public pages won’t show application counts or the submissions playlist. You still see real
                            counts here. Capacity remains enforced server-side.
                        </Typography>
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveMaxParticipants}
                            saving={saving}
                        />
                    </Box>
                ) : (
                    <Box>
                        <Typography sx={{ ...editorialLabelSx, mb: 0.75 }}>Max applications</Typography>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 500, color: midnight.onSurface }}>
                            {maxParticipants != null ? maxParticipants : 'Unlimited'}
                        </Typography>
                        <Typography sx={{ ...editorialLabelSx, mb: 0.75, mt: 2 }}>
                            Hide public submission count
                        </Typography>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 500, color: midnight.onSurface }}>
                            {contest.hide_submissions_count ? 'On' : 'Off'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Jury scoring */}
            <Box sx={sectionShellSx}>
                {sectionHeader('Jury scoring criteria', 'Adjust weights', 'jury', disabled.score_criteria)}
                {editing === 'jury' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {draftScoreCriteria.map((line, idx) => (
                            <Box key={`crit-${idx}`} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={line}
                                    placeholder="Criterion (include % if weighted)"
                                    onChange={e => {
                                        const next = [...draftScoreCriteria];
                                        next[idx] = e.target.value;
                                        setDraftScoreCriteria(next);
                                    }}
                                    sx={{
                                        ...editorialInputSx,
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: midnight.surfaceHighest,
                                            borderRadius: '10px',
                                        },
                                    }}
                                />
                                <IconButton
                                    type="button"
                                    onClick={() =>
                                        setDraftScoreCriteria(draftScoreCriteria.filter((_, i) => i !== idx))
                                    }
                                    aria-label="Remove criterion"
                                    sx={{ color: midnight.onSurfaceVariant, '&:hover': { color: '#ff7351' } }}>
                                    <DeleteOutlineIcon />
                                </IconButton>
                            </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Add criterion"
                                value={newCriterion}
                                onChange={e => setNewCriterion(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const t = newCriterion.trim();
                                        if (t) {
                                            setDraftScoreCriteria([...draftScoreCriteria, t]);
                                            setNewCriterion('');
                                        }
                                    }
                                }}
                                sx={{
                                    ...editorialInputSx,
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: midnight.surfaceHighest,
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                            <IconButton
                                type="button"
                                sx={{ color: midnight.primary }}
                                onClick={() => {
                                    const t = newCriterion.trim();
                                    if (!t) return;
                                    setDraftScoreCriteria([...draftScoreCriteria, t]);
                                    setNewCriterion('');
                                }}>
                                <AddIcon />
                            </IconButton>
                        </Box>
                        <DrawerSectionEditorActions
                            onCancel={cancelEdit}
                            onSave={saveJury}
                            saving={saving}
                            saveDisabled={!!disabled.score_criteria?.disabled}
                        />
                    </Box>
                ) : criteria.length ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {criteria.map((raw, idx) => {
                            const label = String(raw);
                            const w = barWidths[idx] ?? 0;
                            const pctLabel = `${Math.round(w)}%`;
                            return (
                                <Box key={`${idx}-${label.slice(0, 32)}`}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                                fontWeight: 700,
                                                color: midnight.onSurface,
                                                fontSize: 14,
                                                pr: 2,
                                            }}>
                                            {label}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                                fontWeight: 700,
                                                color: midnight.secondary,
                                                fontSize: 14,
                                                flexShrink: 0,
                                            }}>
                                            {pctLabel}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            height: 8,
                                            width: '100%',
                                            borderRadius: 999,
                                            bgcolor: midnight.surfaceHighest,
                                            overflow: 'hidden',
                                        }}>
                                        <Box
                                            sx={{
                                                height: '100%',
                                                width: `${w}%`,
                                                borderRadius: 999,
                                                bgcolor: midnight.secondary,
                                                boxShadow: '0 0 8px rgba(0,237,180,0.35)',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Typography sx={{ color: midnight.onSurfaceVariant, fontStyle: 'italic' }}>
                        No jury criteria configured.
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
